import type { GameState, Move } from './types'
import { getLegalMoves as getPseudoLegalMoves, applyMoveToState, getNextActivePlayer } from './moves'
import { isValidSquare } from './Board'

// ── Internal helpers ──────────────────────────────────────────────────────────

function findKing(state: GameState, playerIndex: number): string | null {
  for (const [key, sq] of Object.entries(state.squares)) {
    if (sq.piece?.type === 'king' && sq.piece.owner === playerIndex) return key
  }
  return null
}

/** Returns true if applying `move` leaves the mover's king in check. */
function wouldLeaveInCheck(state: GameState, move: Move): boolean {
  const next = applyMoveToState(state, move)
  return isInCheck(next, move.player)
}

/**
 * Get castling destination squares for the king.
 * Enforces all four conditions:
 * (a) neither king nor rook has moved
 * (b) no pieces between them
 * (c) king not currently in check
 * (d) king doesn't pass through or land in check
 */
function getCastlingMoves(state: GameState, playerIndex: number, fromKey: string): string[] {
  const sq = state.squares[fromKey]
  if (!sq?.piece || sq.piece.type !== 'king' || sq.piece.hasMoved) return []

  const player = state.players[playerIndex]
  if (!player.castlingRights.kingSide && !player.castlingRights.queenSide) return []
  if (isInCheck(state, playerIndex)) return []

  const { col, row } = sq
  const moves: string[] = []
  const isHorizontal = player.facing === 'UP' || player.facing === 'DOWN'

  function tryDirection(dir: 1 | -1, hasCastlingRight: boolean, horizontal: boolean) {
    if (!hasCastlingRight) return

    const stepKey = (n: number) =>
      horizontal ? `${col + n},${row}` : `${col},${row + n}`

    // Scan outward from king to find the rook; path must be empty
    let dist = 1
    let rookFound = false
    while (true) {
      const rKey = stepKey(dist * dir)
      const [rc, rr] = rKey.split(',').map(Number)
      if (!isValidSquare(rc, rr, state.boardSize)) break
      const rPiece = state.squares[rKey]?.piece
      if (rPiece) {
        if (rPiece.type === 'rook' && rPiece.owner === playerIndex && !rPiece.hasMoved) {
          rookFound = true
        }
        break  // any piece terminates the scan
      }
      dist++
    }
    if (!rookFound) return

    // King moves 2 squares in that direction
    const passKey = stepKey(1 * dir)
    const destKey = stepKey(2 * dir)
    const [dc, dr] = destKey.split(',').map(Number)
    if (!isValidSquare(dc, dr, state.boardSize)) return

    if (
      !wouldLeaveInCheck(state, { player: playerIndex, from: fromKey, to: passKey }) &&
      !wouldLeaveInCheck(state, { player: playerIndex, from: fromKey, to: destKey })
    ) {
      moves.push(destKey)
    }
  }

  if (isHorizontal) {
    tryDirection(+1, player.castlingRights.kingSide, true)
    tryDirection(-1, player.castlingRights.queenSide, true)
  } else {
    // LEFT/RIGHT facing players on 14×14 castle vertically
    tryDirection(+1, player.castlingRights.kingSide, false)
    tryDirection(-1, player.castlingRights.queenSide, false)
  }

  return moves
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Check if a player's king is currently under attack by any opponent piece.
 * Uses pseudo-legal moves to avoid infinite recursion.
 */
export function isInCheck(state: GameState, playerIndex: number): boolean {
  const kingKey = findKing(state, playerIndex)
  if (!kingKey) return false

  for (const [key, sq] of Object.entries(state.squares)) {
    if (!sq.piece || sq.piece.owner === playerIndex) continue
    if (!state.players[sq.piece.owner]?.alive) continue
    const attacks = getPseudoLegalMoves(state, key)
    if (attacks.includes(kingKey)) return true
  }
  return false
}

/**
 * Returns the square keys of all enemy pieces currently giving check to
 * the given player's king. Empty array if not in check.
 */
export function getCheckAttackers(state: GameState, playerIndex: number): string[] {
  const kingKey = findKing(state, playerIndex)
  if (!kingKey) return []

  const attackers: string[] = []
  for (const [key, sq] of Object.entries(state.squares)) {
    if (!sq.piece || sq.piece.owner === playerIndex) continue
    if (!state.players[sq.piece.owner]?.alive) continue
    const attacks = getPseudoLegalMoves(state, key)
    if (attacks.includes(kingKey)) attackers.push(key)
  }
  return attackers
}

/**
 * Fully legal moves: pseudo-legal moves minus those leaving the king in check,
 * plus castling moves for kings.
 * Eliminated players' pieces cannot move.
 */
export function getLegalMoves(state: GameState, fromKey: string): string[] {
  const square = state.squares[fromKey]
  if (!square?.piece) return []

  const playerIndex = square.piece.owner

  // Eliminated players cannot move their pieces
  if (!state.players[playerIndex]?.alive) return []

  const pseudo = getPseudoLegalMoves(state, fromKey)

  const filtered = pseudo.filter(toKey =>
    !wouldLeaveInCheck(state, { player: playerIndex, from: fromKey, to: toKey })
  )

  if (square.piece.type === 'king') {
    filtered.push(...getCastlingMoves(state, playerIndex, fromKey))
  }

  return filtered
}

/**
 * Returns true if the move results in a pawn reaching its promotion rank.
 */
export function isPawnPromotion(state: GameState, move: Move): boolean {
  const sq = state.squares[move.from]
  if (!sq?.piece || sq.piece.type !== 'pawn') return false

  const [toCol, toRow] = move.to.split(',').map(Number)
  const { facing } = sq.piece

  if (state.boardSize === 8) {
    return (facing === 'UP' && toRow === 0) || (facing === 'DOWN' && toRow === 7)
  }

  // 14×14
  if (facing === 'UP') return toRow === 0
  if (facing === 'DOWN') return toRow === 13
  if (facing === 'LEFT') return toCol === 0
  if (facing === 'RIGHT') return toCol === 13
  return false
}

export type CheckStatus = 'normal' | 'check' | 'checkmate' | 'stalemate'

/**
 * Return the check/checkmate/stalemate status for a player.
 */
export function getCheckStatus(state: GameState, playerIndex: number): CheckStatus {
  if (!state.players[playerIndex]?.alive) return 'normal'

  const hasLegalMove = Object.keys(state.squares).some(key => {
    const sq = state.squares[key]
    return sq.piece?.owner === playerIndex && getLegalMoves(state, key).length > 0
  })

  const inCheck = isInCheck(state, playerIndex)

  if (hasLegalMove) return inCheck ? 'check' : 'normal'
  return inCheck ? 'checkmate' : 'stalemate'
}

/**
 * Scan the board for players that should be eliminated.
 *
 * lossCondition === 'king_captured' (default):
 *   - King was captured (no king on board), OR player is in checkmate
 *
 * lossCondition === 'all_pieces':
 *   - All pieces captured (no pieces remain for that player)
 *   - Checkmate is not evaluated in this mode (king is not special)
 *
 * Sets player.alive = false for newly eliminated players.
 */
export function evaluateEliminations(state: GameState): GameState {
  const lossCondition = state.lossCondition ?? 'king_captured'

  if (lossCondition === 'all_pieces') {
    // Count pieces per player
    const pieceCounts = new Map<number, number>()
    for (const sq of Object.values(state.squares)) {
      if (sq.piece) {
        pieceCounts.set(sq.piece.owner, (pieceCounts.get(sq.piece.owner) ?? 0) + 1)
      }
    }
    const players = state.players.map(p => {
      if (!p.alive) return p
      if ((pieceCounts.get(p.index) ?? 0) === 0) return { ...p, alive: false, inCheck: false }
      return p
    })
    return { ...state, players }
  }

  // Default: king_captured
  // Collect which players still have a king
  const playersWithKings = new Set<number>()
  for (const sq of Object.values(state.squares)) {
    if (sq.piece?.type === 'king') playersWithKings.add(sq.piece.owner)
  }

  // First pass: mark players whose king was captured as dead
  let players = state.players.map(p => {
    if (!p.alive) return p
    if (!playersWithKings.has(p.index)) return { ...p, alive: false, inCheck: false }
    return p
  })

  // Second pass: check remaining alive players for checkmate
  // Use intermediate state with phase-1 eliminations applied so that
  // checkmate detection excludes already-eliminated players.
  const intermediate = { ...state, players }
  players = players.map(p => {
    if (!p.alive) return p
    if (getCheckStatus(intermediate, p.index) === 'checkmate') return { ...p, alive: false }
    return p
  })

  return { ...state, players }
}

/**
 * Evaluate whether the game is over based on the current alive player count
 * and the configured elimination mode.
 *
 * @param state       Current game state (after evaluateEliminations)
 * @param capturingPlayer  Index of the player who just moved (used as winner in first_blood)
 */
export function evaluateWinCondition(state: GameState, capturingPlayer: number): GameState {
  const alivePlayers = state.players.filter(p => p.alive)

  // Mutual elimination / all dead → draw
  if (alivePlayers.length === 0) {
    return { ...state, phase: 'gameover', winner: null }
  }

  // One player left → they win
  if (alivePlayers.length === 1) {
    return { ...state, phase: 'gameover', winner: alivePlayers[0].index }
  }

  // first_blood: game ends the moment any player has been eliminated
  if (state.eliminationMode === 'first_blood') {
    const hasEliminated = state.players.some(p => !p.alive)
    if (hasEliminated) {
      return { ...state, phase: 'gameover', winner: capturingPlayer }
    }
  }

  return state
}

/**
 * After evaluateEliminations, the activePlayer may now point at a newly
 * eliminated player. This helper advances past them if needed.
 */
export function advancePastEliminated(state: GameState): GameState {
  if (state.players[state.activePlayer]?.alive) return state
  return {
    ...state,
    activePlayer: getNextActivePlayer(state.players, state.activePlayer),
  }
}
